import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveImg(url) {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

const fadeIn = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const halfSec = { duration: 0.5 };
const viewportOnce = { once: true };

const cardFadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.08 } }),
};

function BlogCard({ post, isFeatured }) {
  return (
    <Link to={`/blog/${post.slug}`} className={`blog-card border border-black bg-white overflow-hidden block group ${isFeatured ? 'md:col-span-2 md:row-span-2' : ''}`} data-testid={`blog-card-${post.slug}`}>
      <div className={`overflow-hidden ${isFeatured ? 'h-64 md:h-80' : 'h-40'}`}>
        <img src={resolveImg(post.image_url)} alt={`electrician Nashville TN - ${post.title}`} className="blog-image w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="font-body text-xs uppercase tracking-widest text-yellow-600 font-semibold">{post.category}</span>
          <span className="font-body text-xs text-zinc-400">{post.date}</span>
        </div>
        <h3 className={`font-heading font-bold uppercase tracking-tight mb-2 ${isFeatured ? 'text-xl' : 'text-sm'}`}>{post.title}</h3>
        <p className={`font-body text-zinc-500 leading-relaxed ${isFeatured ? 'text-sm' : 'text-xs'}`}>{post.excerpt}</p>
        <span className="inline-flex items-center gap-1 mt-4 font-body text-xs uppercase tracking-widest text-zinc-400 group-hover:text-black transition-colors">
          Read More <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

export default function BlogSection() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog?limit=9`);
        const data = res.data;
        setPosts(Array.isArray(data) ? data : data.posts || []);
      } catch (err) {
        logger.error('Blog fetch failed:', err);
      }
    };
    fetchPosts();
  }, []);

  if (!posts.length) return null;

  return (
    <section id="blog" data-testid="blog-section" className="py-24 sm:py-32 px-6 bg-zinc-50 border-t border-zinc-200">
      <div className="max-w-7xl mx-auto">
        <motion.p {...fadeIn} className="font-body text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Our Blog</motion.p>
        <motion.h2 {...fadeInUp} transition={halfSec} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase mb-12">Latest Posts</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {posts.map((post, i) => (
            <motion.div key={post.id} initial="hidden" whileInView="visible" viewport={viewportOnce}
              variants={cardFadeUp} custom={i}>
              <BlogCard post={post} isFeatured={i === 0} />
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/blog" className="inline-flex items-center gap-2 bg-black text-white font-heading font-bold text-xs uppercase tracking-wider px-6 py-3 border border-black hover:-translate-y-0.5 transition-transform" data-testid="blog-view-all-link">
            View All Posts <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </section>
  );
}
